import { Component, Input, ViewChild, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl, NgForm, FormArray } from '@angular/forms';
import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import * as XLSX from 'xlsx';
import { AuthProvider } from '../../providers/auth/auth';
import * as moment from "moment";
import _ from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import { AlertController, LoadingController } from 'ionic-angular';
import { UtilityProvider } from '../../providers/utility/utility';
import { PatientProvider } from '../../providers/patient/patient';
import { DatabaseProvider } from '../../providers/database/database';
import { PdfAdverseEventProvider } from '../../providers/pdf/pdfAdverseEvent';

@Component({
  selector: 'patient-adverse-event',
  templateUrl: 'patient-adverse-event.html'
})
export class PatientAdverseEventComponent implements OnInit {
  private ngUnsubscribe: Subject<any> = new Subject();
  program_id: string;
  program_name: string;
  patient: any;
  exportjson: any;
  private _program_id = new BehaviorSubject<any>([]);
  private _program_name = new BehaviorSubject<any>([]);
  private _patient = new BehaviorSubject<any>([]);


  @Input()
  set programId(value) { this._program_id.next(value); }
  get programId() { return this._program_id.getValue(); }

  @Input()
  set programName(value) { this._program_name.next(value); }
  get programName() { return this._program_name.getValue(); }

  @Input()
  set patientData(value) { this._patient.next(value); }
  get patientData() { return this._patient.getValue(); }

  userid: string;
  user_fullname: string;
  user: any;
  ae_number: string;

  panelStatus = false;

  addeditAdverseEventForm: FormGroup; 
  events: FormArray;
  drugs: FormArray;
  displayedColumns = ['ae_number', 'createdBy.name','actions'];
  dataSource: any;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('formAdverseEventDirective') private formAdverseEventDirective: NgForm;


  constructor(
    private readonly auth: AuthProvider,
    private dbPatient: PatientProvider,
    private db: DatabaseProvider,
    private fb: FormBuilder,
    private pdfAE: PdfAdverseEventProvider,
    public alertCtrl: AlertController,
    public loadingCtrl: LoadingController,
    public utility: UtilityProvider,
  ) {
    this.createForm();

    this.auth.getCurrentUser().then(user => {
      this.userid = user.id;
      this.user_fullname = user.first_name + ' ' + user.last_name;
      this.user = { id: user.id, name: user.first_name + ' ' + user.last_name };

      this.dbPatient.listAdverseEvents(this.program_id, this.patient.id)      
      .snapshotChanges()
      .map(actions => {
        return actions.map(a => {
          const data = a.payload.doc.data() as any;
          data.id = a.payload.doc.id;
          return data;
        });
      })
      .takeUntil(this.ngUnsubscribe)
      .subscribe(adverseevents => {
        this.exportjson = adverseevents;
        this.dataSource = new MatTableDataSource(adverseevents);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
      });
    });
  }

  ngOnInit()  {
    this._program_id
    .takeUntil(this.ngUnsubscribe)
    .subscribe(x => {
      // console.log('Program ID on Summary: ', x)
        this.program_id = x
    });

    this._program_name
    .takeUntil(this.ngUnsubscribe)
    .subscribe(x => {
      // console.log('Program Name on Summary: ', x)
        this.program_name = x
    });

    this._patient
    .takeUntil(this.ngUnsubscribe)
    .subscribe(x => {
      // console.log('Patient on Summary: ', x)
        this.patient = x
    });
  }

  ngOnDestroy() {
    console.log('Leave adverse events and destroy subscriptions');
    this._patient.unsubscribe();
    this._program_id.unsubscribe();
    this._program_name.unsubscribe();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  createForm()  {
    let emailregex: RegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    let phoneregex: RegExp = /^\+[1-9]\d{1,14}(\se?x?t?(\d*))?$/

    this.addeditAdverseEventForm = this.fb.group({
      'ae_number': [null],
      'type': [null],
      'role': [null],
      'reported': [null],
      'pregnant': [null],
      'pregnant_weeks': [null],
      'outinpatient': [null],
      'inpatient_admission_date': [null],
      'concomitant_disease': [null],
      'medhistory_allergies_riskfactors': [null],
      'events': this.fb.array([]),
      'drugs': this.fb.array([]),
      'labresults': [null],
      'comments': [null]
    });
  }

  createEvent(): FormGroup {
    return this.fb.group({
      'name': [null],
      'onset_date': [null],
      'relationship_to_drug': [null],
      'seriousness': [null],
      'outcome': [null],
      'outcome_date': [null],
      'drug_interaction': [null],
      'notes': [null],
      'abate': [null],
      'reappear': [null],
      'dose_reduced': [null],
      'dose_new': [null],
      'product_discontinued': [null],
      'description': [null],
      'cause_of_death': [null],
      'autopsy_results': [null],
    });
  }

  createDrug(): FormGroup {
    return this.fb.group({
      'name': [null],
      'type': [null],
      'route': [null],
      'start_date': [null],
      'end_date': [null],
      'indication': [null],
      'lot': [null],
    });
  }

  addEvent(): void {
    this.events = this.addeditAdverseEventForm.get('events') as FormArray;
    this.events.push(this.createEvent());
  }

  deleteEvent(idx): void {
    this.events.removeAt(idx);
  }

  addDrug(): void {
    this.drugs = this.addeditAdverseEventForm.get('drugs') as FormArray;
    this.drugs.push(this.createDrug());
  }

  deleteDrug(idx): void {
    this.drugs.removeAt(idx);
  }

  getUniqueAENumber()  {
    this.ae_number = "Retrieving...";
    let loading = this.loadingCtrl.create({
      content: "Creating new adverse event number"
    });

    loading.present().then(_ => {
      this.db.getUniqueAENumber(this.program_id).then(data => {
        const aeNumber = 'AE' + this.utility.pad(data, 6, '0');
        this.addeditAdverseEventForm.get('ae_number').setValue(aeNumber);
        loading.dismiss();
      })
      .catch(err => {
        this.ae_number = null;
        this.utility.presentToast('There was an error getting an AE Number: ' + err);
        loading.dismiss();
      })
    });
  }

  save()  {
    let loading = this.loadingCtrl.create({
      content: "Creating new adverse event number"
    });

    loading.present().then(() => {
      let adverseevent = this.addeditAdverseEventForm.value;
      const patient = {
        id: this.patient.id,
        first_name: this.patient.first_name,
        last_name: this.patient.last_name,
        program_patient_id: this.patient.program_patient_id
      }
      adverseevent = { patient: patient, ...adverseevent };
      this.dbPatient.addAdverseEvent(this.program_id, this.patient.id, adverseevent, this.user)
      .then(() => {
        this.utility.presentToast('The adverse event has been saved');
        loading.dismiss();
        this.cancel();
      })
      .catch(err => {
        this.utility.presentToast('There was an error saving the adverse event.  Please try again.');
        loading.dismiss();
      })
    });
  }

  cancel()  {
    this.panelStatus = false;
    setTimeout(() => {
      if (this.events)  {
        while (this.events.length) {
          this.events.removeAt(this.events.length - 1);
        }
      }
      if (this.drugs)  {
        while (this.drugs.length) {
          this.drugs.removeAt(this.drugs.length - 1);
        }
      }
      this.formAdverseEventDirective.resetForm();
      this.addeditAdverseEventForm.reset();
    }, 0);
  }

  viewAE(ae)  {
    this.pdfAE.createAdverseEventForm(this.program_id, this.program_name, ae);
  }


}



// WEBPACK FOOTER //
// ./src/components/patient-adverse-event/patient-adverse-event.ts